import { Action, Step, getOverdueInvoices, overdueToPaymentInvoices } from '../core/utils'
import useResignationAction, { ResignationResult } from '../hooks/useResignationAction'
import { selectWarehouse } from 'containers/Warehouses/warehousesSlice'
import useResignationData from '../hooks/useResignationData'
import { useReduxDispatch, useReduxSelector } from 'store'
import { useHistory, useParams } from 'react-router-dom'
import { getBalance } from 'containers/Login/loginSlice'
import { SECTION_TYPES } from 'containers/Warehouse'
import WarehouseGuard from '../core/WarehouseGuard'
import OutsourcePhoto from '../core/OutsourcePhoto'
import CameraWarning from '../core/CameraWarning'
import usePopupState from 'hooks/usePopupState'
import Loading from 'components/Loading'
import Payments from '../core/Payments'
import React, { useState } from 'react'
import Camera from '../core/Camera'
import Final from '../core/Final'
import Calendar from './Calendar'
import Summary from './Summary'
import Default from './Default'
import Refund from './Refund'
import PayNow from './PayNow'

interface Params {
  id: string
  action: Action
}

interface PayNowState {
  payNow?: payments.TerminationPayNow | null
  /** po platnosci pokaz ekran Final zamiast powrotu do szczegolow */
  canFinish?: boolean
  /** zaleglosci z wyceny sprzed zapisu - placone razem z dokumentem "Zaplac teraz" */
  overdueInvoices: payments.QuoteOverdueInvoice[]
}

const MagResignationContent: React.FC = () => {
  const { id, action } = useParams<Params>()
  const warehouse = useReduxSelector(state => selectWarehouse(state, id))

  const [step, setStep] = useState<Step>('init')
  const [deliveries, setDeliveries] = useState<payments.Delivery[]>([])
  const [showCalendar] = useState(!warehouse.resignation)
  const [dateError, setDateError] = useState<string | undefined>(undefined)
  const [payNowState, setPayNowState] = useState<PayNowState | undefined>(undefined)
  const { isLoading, response, quoteDate, selectedData, selectData, reload } = useResignationData('termination', 'finishRenting', deliveries)
  const { isSending, sendResignation } = useResignationAction('termination', selectedData, deliveries, setStep)
  const { popup, openPopup } = usePopupState<{ type: 'photoWarning' }>()

  const { goBack, replace, push } = useHistory()
  const dispatch = useReduxDispatch()

  const calendarPath = `/warehouses/${id}/${SECTION_TYPES.termination}/calendar`
  const getDateError = (result: ResignationResult) => result.errors?.date?.[0] || result.message

  /**
   * Zapis zakonczenia z kalendarza (ekran podsumowania zostal usuniety):
   * - auto (faktury wg harmonogramu): hook zapisuje i wraca do szczegolow wynajmu
   * - payNow: zapis + przejscie do platnosci za nowy dokument i zaleglosci
   */
  /** dokument do zaplaty po zapisie (wybrane "Zaplac teraz" albo obowiazkowa faktura "teraz") */
  const goToPayNow = (result: ResignationResult, overdueInvoices: payments.QuoteOverdueInvoice[]) => {
    setPayNowState({ payNow: result.termination?.payNow, overdueInvoices, canFinish: result.canFinish })
    setStep('payNow')
  }

  const finishRental = async (settlement?: payments.TerminationSettlement) => {
    const overdueInvoices = getOverdueInvoices(response)
    const result = await sendResignation(settlement)
    if (result.ok) {
      if (result.awaitingPayment) goToPayNow(result, overdueInvoices)
    } else if (!result.skipped) {
      setDateError(getDateError(result))
    }
  }

  const sendResignationFromForm = async () => {
    const overdueInvoices = getOverdueInvoices(response)
    const result = await sendResignation()
    if (result.ok && result.awaitingPayment) {
      goToPayNow(result, overdueInvoices)
    } else if (!result.ok && result.errors?.date) {
      setDateError(getDateError(result))
      push(calendarPath)
    }
  }

  const selectDate = (prev: React.SetStateAction<payments.SelectedData>) => {
    selectData(prev)
    setDateError(undefined)
  }

  const calendar = response && <Calendar
    response={response}
    quoteDate={quoteDate}
    isLoading={isLoading}
    isSending={isSending}
    selectedData={selectedData}
    selectData={selectDate}
    error={dateError}
    onSubmit={finishRental}
  />

  return (
    <div className='menu__section menu__resignation'>
      {popup?.type === 'photoWarning' && <div>
        <CameraWarning
          date={selectedData.date}
          redirect={`/warehouses/${id}/${SECTION_TYPES.termination}/camera`}
          showOutsourcePhotoModal={() => {
            replace(`/warehouses/${id}/${SECTION_TYPES.termination}/outsourcePhoto`)
          }}
        />
      </div>}

      {(() => {
        switch (step) {
          case 'finish': return <Final />

          // platnosc zaleglych faktur TEJ umowy (z wyceny, a nie z globalnego /balance)
          case 'payment': return <Payments
            invoices={overdueToPaymentInvoices(getOverdueInvoices(response))}
            selectedData={selectedData}
            close={remotePaymentId => {
              if (remotePaymentId) {
                dispatch(getBalance())
                reload()
              }
              setStep('init')
            }}
          />

          // "Zaplac teraz" - zakonczenie juz zapisane, czekamy na dokument i placimy
          case 'payNow': return <PayNow
            contractId={warehouse.contractId}
            payNow={payNowState?.payNow}
            overdueInvoices={payNowState?.overdueInvoices ?? []}
            selectedData={selectedData}
            close={remotePaymentId => {
              if (remotePaymentId) dispatch(getBalance())
              if (payNowState?.canFinish) setStep('finish')
              else goBack()
            }}
          />

          default: {
            switch (action) {
              case 'calendar': return calendar || <div className='resignation__main'>
                <Loading middleOfPage />
              </div>

              case 'camera': return <Camera
                selectedData={selectedData}
                selectData={selectData}
                showOutsourcePhotoModal={() => {
                  replace(`/warehouses/${id}/${SECTION_TYPES.termination}/outsourcePhoto`)
                }}
              />

              case 'outsourcePhoto': return <OutsourcePhoto
                date={selectedData.date}
              />

              case 'summary': return <Summary
                response={response}
                setStep={setStep}
              />

              case 'refund': return <Refund
                response={response}
                selectedData={selectedData}
                selectData={selectData}
              />

              default: return response
                ? showCalendar
                  ? calendar
                  : <Default
                    response={response}
                    isSending={isSending}
                    deliveries={deliveries}
                    selectedData={selectedData}
                    setDeliveries={setDeliveries}
                    sendResignation={sendResignationFromForm}
                    selectData={selectData}
                    setStep={setStep}
                    showCameraWarning={() => {
                      openPopup({ type: 'photoWarning' })
                    }}
                  />
                : <div className='resignation__main'>
                  <Loading middleOfPage />
                </div>
            }
          }
        }
      })()}
    </div>
  )
}

const MagResignation: React.FC = () => (
  <WarehouseGuard>
    <MagResignationContent />
  </WarehouseGuard>
)

export default MagResignation
